import { useState, useMemo } from "react";
import { ChevronDown, ChevronRight, Globe, ShoppingBag, BookOpen, Settings, Users, Play, MapPin, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { CATEGORY_HIERARCHY, groupCategoriesByParent, type ParentCategory } from "@/config/categoryHierarchy";
import { cn } from "@/lib/utils";

interface CategoryAccordionProps {
  blockedCategories: string[];
  onCategoriesChange: (categories: string[]) => void;
  readOnly?: boolean;
  searchQuery?: string;
}

const ICON_MAP: Record<string, React.ElementType> = {
  Globe,
  ShoppingBag,
  BookOpen,
  Settings,
  Users,
  Play,
  MapPin,
  AlertTriangle
};

export const CategoryAccordion = ({
  blockedCategories,
  onCategoriesChange,
  readOnly = false,
  searchQuery = ""
}: CategoryAccordionProps) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const handleSubcategoryToggle = (subcategoryName: string) => {
    if (readOnly) return;
    
    const isBlocked = blockedCategories.includes(subcategoryName);
    if (isBlocked) {
      onCategoriesChange(blockedCategories.filter(c => c !== subcategoryName));
    } else {
      onCategoriesChange([...blockedCategories, subcategoryName]);
    }
  };

  const handleParentToggle = (parent: ParentCategory) => {
    if (readOnly) return;
    
    const subcategoryNames = parent.subcategories.map(s => s.name);
    const allBlocked = subcategoryNames.every(name => blockedCategories.includes(name));
    
    if (allBlocked) {
      // Allow all subcategories
      onCategoriesChange(blockedCategories.filter(c => !subcategoryNames.includes(c)));
    } else {
      // Block all subcategories
      const newBlocked = [...blockedCategories];
      subcategoryNames.forEach(name => {
        if (!newBlocked.includes(name)) {
          newBlocked.push(name);
        }
      });
      onCategoriesChange(newBlocked);
    }
  };

  const groupedCategories = useMemo(() => {
    return groupCategoriesByParent(blockedCategories);
  }, [blockedCategories]);

  const filteredGroups = useMemo(() => {
    if (!searchQuery.trim()) return groupedCategories;
    
    const query = searchQuery.toLowerCase();
    return groupedCategories
      .map(group => ({
        ...group,
        subcategories: group.subcategories.filter(sub =>
          sub.name.toLowerCase().includes(query) ||
          sub.examples?.some(ex => ex.toLowerCase().includes(query))
        )
      }))
      .filter(group => 
        group.name.toLowerCase().includes(query) || 
        group.subcategories.length > 0
      );
  }, [groupedCategories, searchQuery]);

  return (
    <div className="space-y-2">
      {filteredGroups.map(group => {
        const Icon = ICON_MAP[group.icon] || Globe;
        const isExpanded = expandedGroups.includes(group.name);
        const isUniform = group.isAllBlocked || group.isAllAllowed;
        
        return (
          <div key={group.name} className="border rounded-lg overflow-hidden">
            {/* Parent Row */}
            <div
              className="flex items-center gap-3 py-4 px-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => toggleGroup(group.name)}
            >
              <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{group.name}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {group.description}
                </div>
              </div>

              {/* Status Indicator */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {isUniform ? (
                  <Switch
                    checked={group.isAllAllowed}
                    onCheckedChange={() => handleParentToggle(group)}
                    disabled={readOnly}
                    onClick={(e) => e.stopPropagation()}
                    variant="success-danger"
                  />
                ) : (
                  <span className="text-xs font-medium">
                    <span className="text-green-600">{group.allowedCount}</span>
                    {" | "}
                    <span className="text-red-600">{group.blockedCount}</span>
                  </span>
                )}
                
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </div>
            </div>

            {/* Subcategories */}
            {isExpanded && (
              <div className="pl-8 pr-4 py-3 space-y-2 bg-background">
                {group.subcategories.map(sub => {
                  const isBlocked = blockedCategories.includes(sub.name);
                  const isAllowed = !isBlocked;
                  
                  return (
                    <div
                      key={sub.name}
                      className={cn(
                        "p-3 rounded-lg border transition-all animate-fade-in",
                        isAllowed
                          ? "bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-900"
                          : "bg-red-50 border-red-200 dark:bg-red-950/20 dark:border-red-900"
                      )}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm">{sub.name}</div>
                          {sub.examples && sub.examples.length > 0 && (
                            <div className="text-xs text-muted-foreground truncate mt-0.5">
                              {sub.examples.join(", ")}
                            </div>
                          )}
                        </div>
                        
                        <Switch
                          checked={isAllowed}
                          onCheckedChange={() => handleSubcategoryToggle(sub.name)}
                          disabled={readOnly}
                          variant="success-danger"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {filteredGroups.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm">
          No categories match your search
        </div>
      )}
    </div>
  );
};
